import {
  ContractDataFieldSectionDto,
  ContractTemplateDto,
  CustomAmountFieldDto,
  CustomBooleanFieldDto,
  CustomDateFieldDto,
  CustomEmailFieldDto,
  CustomEnumFieldDto,
  CustomMultiEnumFieldDto,
  CustomNumberFieldDto,
  CustomPercentageFieldDto,
  CustomPersonFieldDto,
  CustomPhoneFieldDto,
  CustomRichTextFieldDto,
  CustomTagFieldDto,
  CustomTextFieldDto,
  CustomUrlFieldDto,
  NumberFieldDto,
  Populater,
  ProductCommentDto,
} from "../autogen/bff-api";

export interface ProductGroup {
  id: string;
  sourcingEventId?: string | null;
  projectId?: string | null;
  name: string;
  products?: Product[];
  productFields: ProductTemplateField[];
  originProductGroupId?: string | null;
}

export type ProductGroupDraft = Omit<ProductGroup, "id">;

export interface Product {
  id: string;
  quantity: NumberFieldDto;
  unitPrice?: NumberFieldDto;
  productFields: ProductField[];
  conversation?: ProductCommentDto[];
}

export type ProductDraft = Omit<Product, "id"> & { id?: string };

export type ProductFieldType = "Text" | "Number" | "Boolean" | "Quantity";

export const productFieldTypes: ProductFieldType[] = ["Text", "Boolean", "Number"];

export interface ProductTemplateField {
  id: string;
  name: string;
  type: ProductFieldType;
  populatedBy: Populater;
  isRequired: boolean;
}

export type ProductField = ProductTemplateField & { value?: number | string | boolean | null };

export const isProductDraft = (product: Product | ProductDraft) => {
  return !(product as Product).id;
};

export enum FetchState {
  Idle = "Idle",
  Loading = "Loading",
  Error = "Error",
  Success = "Success",
}

export interface CustomContractFieldSections {
  [id: string]: ContractDataFieldSection;
}

export type ContractTemplate = Omit<ContractTemplateDto, "requiredCustomDataFields"> & {
  requiredCustomDataFields: CustomContractFieldSections;
};

export type CounterpartyContext =
  | {
      organizationEntryId: string;
      organizationId?: never;
    }
  | {
      organizationId: string;
      organizationEntryId?: never;
    };

export enum CustomFieldType {
  CustomNumber = "CustomNumber",
  CustomAmount = "CustomAmount",
  CustomDate = "CustomDate",
  CustomEmail = "CustomEmail",
  CustomBoolean = "CustomBoolean",
  CustomEnum = "CustomEnum",
  CustomMultiEnum = "CustomMultiEnum",
  CustomPercentage = "CustomPercentage",
  CustomPerson = "CustomPerson",
  CustomPhone = "CustomPhone",
  CustomRichText = "CustomRichText",
  CustomText = "CustomText",
  CustomTag = "CustomTag",
  CustomUrl = "CustomUrl",
}

export type CustomContractField =
  | CustomAmountField
  | CustomNumberField
  | CustomBooleanField
  | CustomDateField
  | CustomEmailField
  | CustomEnumField
  | CustomMultiEnumField
  | CustomPercentageField
  | CustomPersonField
  | CustomPhoneField
  | CustomRichTextField
  | CustomTextField
  | CustomUrlField
  | CustomTagField;

export interface ContractDataFieldSection extends ContractDataFieldSectionDto {
  fields: CustomContractField[];
}

export interface CustomNumberField extends CustomNumberFieldDto {
  type: CustomFieldType.CustomNumber;
}

export interface CustomAmountField extends CustomAmountFieldDto {
  type: CustomFieldType.CustomAmount;
}

export interface CustomDateField extends CustomDateFieldDto {
  type: CustomFieldType.CustomDate;
}

export interface CustomEmailField extends CustomEmailFieldDto {
  type: CustomFieldType.CustomEmail;
}

export interface CustomBooleanField extends CustomBooleanFieldDto {
  type: CustomFieldType.CustomBoolean;
}

export interface CustomEnumField extends CustomEnumFieldDto {
  type: CustomFieldType.CustomEnum;
}

export interface CustomMultiEnumField extends CustomMultiEnumFieldDto {
  type: CustomFieldType.CustomMultiEnum;
}

export interface CustomPercentageField extends CustomPercentageFieldDto {
  type: CustomFieldType.CustomPercentage;
}

export interface CustomPersonField extends CustomPersonFieldDto {
  type: CustomFieldType.CustomPerson;
}

export interface CustomPhoneField extends CustomPhoneFieldDto {
  type: CustomFieldType.CustomPhone;
}

export interface CustomRichTextField extends CustomRichTextFieldDto {
  type: CustomFieldType.CustomRichText;
}

export interface CustomTagField extends CustomTagFieldDto {
  type: CustomFieldType.CustomTag;
}

export interface CustomUrlField extends CustomUrlFieldDto {
  type: CustomFieldType.CustomUrl;
}

export interface CustomTextField extends CustomTextFieldDto {
  type: CustomFieldType.CustomText;
}

// Type guards for custom contract fields
export const isCustomAmountField = (field: CustomContractField): field is CustomAmountField => {
  return field.type === CustomFieldType.CustomAmount;
};

export const isCustomNumberField = (field: CustomContractField): field is CustomNumberField => {
  return field.type === CustomFieldType.CustomNumber;
};

export const isCustomBooleanField = (field: CustomContractField): field is CustomBooleanField => {
  return field.type === CustomFieldType.CustomBoolean;
};

export const isCustomDateField = (field: CustomContractField): field is CustomDateField => {
  return field.type === CustomFieldType.CustomDate;
};

export const isCustomEmailField = (field: CustomContractField): field is CustomEmailField => {
  return field.type === CustomFieldType.CustomEmail;
};

export const isCustomEnumField = (field: CustomContractField): field is CustomEnumField => {
  return field.type === CustomFieldType.CustomEnum;
};

export const isCustomMultiEnumField = (field: CustomContractField): field is CustomMultiEnumField => {
  return field.type === CustomFieldType.CustomMultiEnum;
};

export const isCustomPercentageField = (field: CustomContractField): field is CustomPercentageField => {
  return field.type === CustomFieldType.CustomPercentage;
};

export const isCustomPersonField = (field: CustomContractField): field is CustomPersonField => {
  return field.type === CustomFieldType.CustomPerson;
};

export const isCustomPhoneField = (field: CustomContractField): field is CustomPhoneField => {
  return field.type === CustomFieldType.CustomPhone;
};

export const isCustomRichTextField = (field: CustomContractField): field is CustomRichTextField => {
  return field.type === CustomFieldType.CustomRichText;
};

export const isCustomTagField = (field: CustomContractField): field is CustomTagField => {
  return field.type === CustomFieldType.CustomTag;
};

export const isCustomTextField = (field: CustomContractField): field is CustomTextField => {
  return field.type === CustomFieldType.CustomText;
};

export const isCustomUrlField = (field: CustomContractField): field is CustomUrlField => {
  return field.type === CustomFieldType.CustomUrl;
};

// Shared JSON Schema types for AI response schemas
export type JsonSchemaProperty =
  | ObjectSchemaProperty
  | ArraySchemaProperty
  | StringSchemaProperty
  | NumberSchemaProperty
  | BooleanSchemaProperty;

export interface ObjectSchemaProperty {
  type: "object";
  properties: Record<string, JsonSchemaProperty>;
  required?: string[];
  additionalProperties: false;
}

export interface ArraySchemaProperty {
  type: "array";
  items: JsonSchemaProperty;
}

export interface StringSchemaProperty {
  type: "string";
  enum?: string[];
}

export interface NumberSchemaProperty {
  type: "number";
  minimum?: number;
  maximum?: number;
}

export interface BooleanSchemaProperty {
  type: "boolean";
}

export interface JsonSchema extends ObjectSchemaProperty {
  type: "object";
  properties: Record<string, JsonSchemaProperty>;
  required: string[];
  additionalProperties: false;
}

export interface JsonSchemaDto {
  name: string;
  schema: string;
  strict: boolean;
}

export interface RequirementFulfillment {
  requirement: string;
  fulfilled: "yes" | "no" | "partly";
}

export interface BidEvaluation {
  bidId: string;
  score: number;
  strengths: string[];
  weaknesses: string[];
  recommendation: Recommendation;
  comments: string;
  totalPrice: number;
  requirements: RequirementFulfillment[];
}

export type Recommendation = "recommended" | "needs clarifications" | "not recommended";

export interface EvaluationSummary {
  bestBid: string;
  executiveSummary: string;
  overallRecommendation: string;
  keyInsights: string[];
}

export interface ComparisonResult {
  summary: EvaluationSummary;
  perBidSummary: {
    bidId: string;
    comparisonSummary: string;
    keyInsights: string[];
    recommendation: {
      isRecommended: boolean;
      rationale: string;
    };
  }[];
}

export interface SingleEvaluationResult {
  evaluation: BidEvaluation;
}

export interface RequirementsResult {
  requirements: string[];
}

export const createSingleBidEvaluationSchema = (): JsonSchemaDto => {
  const schema: JsonSchema = {
    type: "object",
    properties: {
      evaluation: {
        type: "object",
        properties: {
          bidId: { type: "string" },
          score: { type: "number", minimum: 0, maximum: 100 },
          strengths: {
            type: "array",
            items: { type: "string" },
          },
          weaknesses: {
            type: "array",
            items: { type: "string" },
          },
          recommendation: { type: "string", enum: ["recommended", "needs clarifications", "not recommended"] },
          comments: { type: "string" },
          totalPrice: { type: "number", minimum: 0 },
          requirements: {
            type: "array",
            items: {
              type: "object",
              properties: {
                requirement: { type: "string" },
                fulfilled: { type: "string", enum: ["yes", "no", "partly"] },
              },
              required: ["requirement", "fulfilled"],
              additionalProperties: false,
            },
          },
        },
        required: [
          "bidId",
          "score",
          "strengths",
          "weaknesses",
          "recommendation",
          "comments",
          "totalPrice",
          "requirements",
        ],
        additionalProperties: false,
      },
    },
    required: ["evaluation"],
    additionalProperties: false,
  };

  return {
    name: "SingleBidEvaluationSchema_v4",
    schema: JSON.stringify(schema),
    strict: true,
  };
};

export const comparisonSchema: JsonSchemaDto = {
  name: "BidsComparisonSchema_v7",
  strict: true,
  schema: JSON.stringify({
    type: "object",
    properties: {
      summary: {
        type: "object",
        properties: {
          bestBid: { type: ["string", "null"] },
          overallRecommendation: { type: "string" },
          executiveSummary: {
            type: "string",
            description: `
            A short summary of the sourcing event, the bids delivered and what makes the best bid the best bid.
              
            Bid ids should not be included.
              `,
          },
          keyInsights: {
            type: "array",
            items: { type: "string" },
          },
        },
        required: ["bestBid", "overallRecommendation", "executiveSummary", "keyInsights"],
        additionalProperties: false,
      },
      perBidSummary: {
        type: "array",
        items: {
          type: "object",
          properties: {
            bidId: {
              type: "string",
              description: "The UUID of the bid this summary refers to",
            },
            comparisonSummary: {
              type: "string",
              description: "How this bid compares to the other bids",
            },
            keyInsights: {
              type: "array",
              description: "Key insights specific to this bid",
              items: { type: "string" },
            },
            recommendation: {
              type: "object",
              description: "Whether this bid/supplier is recommended and why",
              properties: {
                isRecommended: {
                  type: "boolean",
                  description: "True if this bid/supplier is recommended overall",
                },
                rationale: {
                  type: "string",
                  description: "Explanation of why it is or isn't recommended",
                },
              },
              required: ["isRecommended", "rationale"],
              additionalProperties: false,
            },
          },
          required: ["bidId", "comparisonSummary", "keyInsights", "recommendation"],
          additionalProperties: false,
        },
      },
    },
    required: ["summary", "perBidSummary"],
    additionalProperties: false,
  }),
};

export const requirementsSchema: JsonSchemaDto = {
  name: "RequirementsSchema_v1",
  schema: JSON.stringify({
    type: "object",
    properties: {
      requirements: { type: "array", items: { type: "string" }, minItems: 1, maxItems: 10 },
    },
    required: ["requirements"],
    additionalProperties: false,
  }),
  strict: true,
};
